import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import { Sliders, Save, RotateCcw, Rocket, BadgeCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["MA", "DZ", "TN"];
const DEFAULTS = {
  boost: {
    MA: { amount: 99, currency: "mad", days: 7, label: "Boost 7j (Maroc)" },
    DZ: { amount: 1500, currency: "dzd", days: 7, label: "Boost 7j (Algérie)" },
    TN: { amount: 30, currency: "tnd", days: 7, label: "Boost 7j (Tunisie)" },
  },
  pro_subscription: {
    MA: { amount: 199, currency: "mad", label: "Abonnement Pro mensuel (Maroc)" },
    DZ: { amount: 3000, currency: "dzd", label: "Abonnement Pro mensuel (Algérie)" },
    TN: { amount: 60, currency: "tnd", label: "Abonnement Pro mensuel (Tunisie)" },
  },
};

export default function AdminSettings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/login");
    if (user.role !== "admin") return navigate("/dashboard");
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/packages");
        setPackages(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [user, loading, navigate]);

  const updateField = (kind, country, field, value) => {
    setPackages(p => ({
      ...p,
      [kind]: { ...p[kind], [country]: { ...p[kind][country], [field]: value } },
    }));
    setDirty(true);
  };

  const resetToDefaults = () => {
    setPackages(JSON.parse(JSON.stringify(DEFAULTS)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/packages", packages);
      setPackages(data);
      setDirty(false);
      toast.success("Tarifs mis à jour. Les nouveaux paiements utilisent ces valeurs.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== "admin") return null;

  if (!packages) {
    return (
      <Layout withFooter={false}>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="h-12 w-96 rounded-md bg-[#F0EBE6] animate-pulse mb-8" data-testid="admin-settings-loading" />
          <div className="space-y-4">
            {[0,1].map(i => <div key={i} className="h-64 rounded-2xl bg-[#F5F0EB] animate-pulse" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout withFooter={false}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-settings">
        <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Admin · Paramètres</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2 flex items-center gap-3">
          Paramètres plateforme <Sliders className="w-8 h-8 text-[#D6202C]" />
        </h1>
        <p className="text-muted-foreground mb-8">
          Tarifs Boost et Abonnement Pro par pays. Modifications appliquées en temps réel sur les prochains paiements Stripe.
        </p>

        {/* Boost */}
        <PackageSection
          title="Boost d'annonce"
          subtitle="Paiement unique pour mettre en avant une annonce pendant une durée donnée."
          icon={<Rocket className="w-5 h-5" />}
          color="#D6202C"
          fields={[
            { key: "amount", label: "Montant", type: "number", step: "0.01" },
            { key: "currency", label: "Devise", type: "text" },
            { key: "days", label: "Durée (jours)", type: "number", step: "1" },
            { key: "label", label: "Libellé", type: "text" },
          ]}
          data={packages.boost}
          kind="boost"
          onChange={updateField}
        />

        {/* Pro Subscription */}
        <PackageSection
          title="Abonnement Pro"
          subtitle="Abonnement mensuel pour devenir agence vérifiée (badge Pro)."
          icon={<BadgeCheck className="w-5 h-5" />}
          color="#11783A"
          fields={[
            { key: "amount", label: "Montant", type: "number", step: "0.01" },
            { key: "currency", label: "Devise", type: "text" },
            { key: "label", label: "Libellé", type: "text" },
          ]}
          data={packages.pro_subscription}
          kind="pro_subscription"
          onChange={updateField}
        />

        {/* Bar actions */}
        <div className="sticky bottom-4 bg-white border border-[#E5E5E5] rounded-2xl p-4 shadow-lg flex items-center justify-between" data-testid="admin-settings-actions">
          <div className="flex items-center gap-2 text-sm">
            {dirty ? (
              <span className="text-[#D6202C] flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Modifications non sauvegardées
              </span>
            ) : (
              <span className="text-muted-foreground">Aucune modification en attente</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={resetToDefaults}
              data-testid="admin-settings-reset"
              className="rounded-xl"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" /> Tarifs par défaut
            </Button>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-settings-save"
              className="rounded-xl bg-[#11783A] hover:bg-[#0d5e2d]"
            >
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const PackageSection = ({ title, subtitle, icon, color, fields, data, kind, onChange }) => (
  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5 mb-4" data-testid={`section-${kind}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>
        {icon}
      </div>
      <div>
        <h2 className="font-heading text-xl font-medium">{title}</h2>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </div>
    <div className="space-y-4">
      {COUNTRIES.map((c) => (
        <div key={c} className="border-t border-[#F5F0EB] pt-4">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <span>{COUNTRY_FLAGS[c]}</span> {COUNTRY_LABELS[c]}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-[11px] text-muted-foreground block mb-1">{f.label}</label>
                <Input
                  type={f.type}
                  step={f.step}
                  value={data[c]?.[f.key] ?? ""}
                  onChange={(e) => {
                    const v = f.type === "number" ? Number(e.target.value) : e.target.value;
                    onChange(kind, c, f.key, v);
                  }}
                  data-testid={`field-${kind}-${c}-${f.key}`}
                  className="rounded-xl text-sm h-9"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);
